import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldWarning, CheckCircle, Trash, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AccessDenied } from "@/components/AccessDenied";
import { Checkbox } from "@/components/ui/checkbox";
import { formatLocal } from "@/lib/timezone";

const BULK_CAP = 50;

const SortTh = ({ sortKey, label, current, dir, onSort, className = "" }) => (
  <th
    className={`px-4 py-3 cursor-pointer select-none hover:text-[#0A0A0A] ${className}`}
    onClick={() => onSort(sortKey)}
    scope="col"
    aria-sort={current === sortKey ? (dir === "asc" ? "ascending" : "descending") : "none"}
  >
    {label}{current === sortKey ? (dir === "asc" ? " ▲" : " ▼") : ""}
  </th>
);

export default function QuarantineLeadsPage() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [working, setWorking] = useState({});   // lead_id → true while an API call is in-flight
  const [selected, setSelected] = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [reasonFilter, setReasonFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sortKey, setSortKey] = useState("quarantined_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/leads/quarantine");
      setLeads(r.data || []);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
    setLoading(false);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, []);

  const sources = useMemo(
    () => Array.from(new Set(leads.map(l => l.source_of_lead).filter(Boolean))).sort(),
    [leads]
  );

  const filtered = useMemo(() => {
    let rows = leads;
    if (reasonFilter.trim()) {
      const q = reasonFilter.trim().toLowerCase();
      rows = rows.filter(l => (l.quarantine_reason || "").toLowerCase().includes(q));
    }
    if (sourceFilter) {
      rows = rows.filter(l => l.source_of_lead === sourceFilter);
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [leads, reasonFilter, sourceFilter, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  // Selection is scoped to the current filtered result set. Clear whenever
  // the filters change so a stale selection can't carry over silently.
  useEffect(() => { setSelected(new Set()); }, [reasonFilter, sourceFilter]);

  const toggleSelected = (leadId) => setSelected(s => {
    const n = new Set(s);
    if (n.has(leadId)) n.delete(leadId); else n.add(leadId);
    return n;
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every(l => selected.has(l.lead_id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) { setSelected(new Set()); return; }
    setSelected(new Set(filtered.map(l => l.lead_id)));
  };

  const review = async (lead, action) => {
    const verb = action === "release" ? "release" : "delete";
    const msg = action === "release"
      ? `Release "${lead.company_name || lead.contact_email}" from quarantine?\nThis re-activates the lead and makes it eligible for campaigns.`
      : `Permanently delete "${lead.company_name || lead.contact_email}" from the system?\nThis action cannot be undone.`;
    if (!window.confirm(msg)) return;
    setWorking(w => ({ ...w, [lead.lead_id]: true }));
    try {
      await api.post(`/crm/leads/${lead.lead_id}/quarantine/review`, { action });
      setLeads(prev => prev.filter(l => l.lead_id !== lead.lead_id));
      toast.success(action === "release"
        ? `Released "${lead.company_name || lead.contact_email}"`
        : `Deleted "${lead.company_name || lead.contact_email}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${verb}`);
    }
    setWorking(w => { const n = { ...w }; delete n[lead.lead_id]; return n; });
  };

  const bulkReview = async (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const msg = action === "release"
      ? `Release ${ids.length} lead${ids.length === 1 ? "" : "s"} from quarantine?\nThey re-activate and become eligible for campaigns again.`
      : `Permanently delete ${ids.length} lead${ids.length === 1 ? "" : "s"} from the system?\nThis action cannot be undone.`;
    if (!window.confirm(msg)) return;
    if (ids.length > BULK_CAP && !window.confirm(
      `You're about to ${action} ${ids.length} leads at once — more than the usual ${BULK_CAP}-lead batch. Continue?`
    )) return;
    setBulkWorking(true);
    try {
      const res = await api.post("/crm/leads/quarantine/bulk-review", { lead_ids: ids, action });
      const d = res.data;
      toast.success(`${action === "release" ? "Released" : "Deleted"} ${d.succeeded} · failed ${d.failed}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkWorking(false);
    }
  };

  if (denied) return <AccessDenied module="Quarantine" />;

  return (
    <div className="p-6" data-testid="quarantine-leads-page">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => nav("/crm/leads")}
          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] transition-colors"
          data-testid="quarantine-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Sales Leads
        </button>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <ShieldWarning size={28} weight="fill" className="text-[#D97706]" />
        <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]">Quarantine</h1>
      </div>
      <p className="text-sm text-[#525252] max-w-2xl mb-6">
        Inbound leads flagged as solicitation, spam, or bot submissions sit here until you review them.
        While a lead is quarantined it is <strong>excluded from every outbound campaign</strong>
        — no emails or reminders are sent. Release to re-activate or Delete to remove permanently.
      </p>

      {loading ? (
        <p className="text-sm text-[#A3A3A3]">Loading…</p>
      ) : leads.length === 0 ? (
        <div className="bg-[#F0FDF4] border-l-4 border-[#16A34A] p-5 max-w-xl">
          <p className="text-[#166534] font-semibold text-sm">Nothing quarantined right now.</p>
          <p className="text-[#166534]/70 text-xs mt-1">
            When a lead arrives that matches a solicitation pattern, it'll appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label htmlFor="quarantine-reason-filter" className="block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                Filter by reason
              </label>
              <input
                id="quarantine-reason-filter"
                type="text"
                value={reasonFilter}
                onChange={e => setReasonFilter(e.target.value)}
                placeholder="e.g. disposable, honeypot…"
                className="border border-[#E5E5E5] px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0055FF] min-w-[220px]"
                data-testid="quarantine-reason-filter"
              />
            </div>
            <div>
              <label htmlFor="quarantine-source-filter" className="block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                Filter by source
              </label>
              <select
                id="quarantine-source-filter"
                value={sourceFilter}
                onChange={e => setSourceFilter(e.target.value)}
                className="border border-[#E5E5E5] px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]"
                data-testid="quarantine-source-filter"
              >
                <option value="">All sources</option>
                {sources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="text-xs text-[#A3A3A3] pb-1.5">
              {filtered.length} of {leads.length} quarantined lead{leads.length === 1 ? "" : "s"}
            </div>
          </div>

          {selected.size > 0 && (
            <div
              className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 bg-[#0A0A0A] text-white"
              role="toolbar"
              aria-label="Bulk quarantine actions"
              data-testid="quarantine-bulk-toolbar"
            >
              <span className="text-sm font-semibold" aria-live="polite" data-testid="quarantine-selected-count">
                {selected.size} lead{selected.size === 1 ? "" : "s"} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#D4D4D4] hover:text-white"
                  data-testid="quarantine-clear-selection-btn"
                >
                  Clear
                </button>
                <button
                  onClick={() => bulkReview("release")}
                  disabled={bulkWorking}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                  data-testid="quarantine-bulk-release-btn"
                >
                  <CheckCircle size={13} weight="bold" /> Release Selected ({selected.size})
                </button>
                <button
                  onClick={() => bulkReview("delete")}
                  disabled={bulkWorking}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                  data-testid="quarantine-bulk-delete-btn"
                >
                  <Trash size={13} weight="bold" /> Delete Selected ({selected.size})
                </button>
              </div>
            </div>
          )}

          <div className="border border-[#E5E5E5] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                  <th className="px-4 py-3 w-10">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label={allFilteredSelected ? "Clear selection" : "Select all filtered leads"}
                      data-testid="quarantine-select-all"
                    />
                  </th>
                  <SortTh sortKey="company_name" label="Company" current={sortKey} dir={sortDir} onSort={onSort} />
                  <th className="px-4 py-3">Contact</th>
                  <SortTh sortKey="source_of_lead" label="Source" current={sortKey} dir={sortDir} onSort={onSort} />
                  <th className="px-4 py-3">Reason</th>
                  <SortTh sortKey="quarantined_at" label="Quarantined" current={sortKey} dir={sortDir} onSort={onSort} className="whitespace-nowrap" />
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(l => {
                  const busy = Boolean(working[l.lead_id]);
                  return (
                  <tr key={l.lead_id} className={`border-b border-[#F5F5F5] hover:bg-[#FAFAFA] ${selected.has(l.lead_id) ? "bg-blue-50/40" : ""}`} data-testid={`quarantine-row-${l.lead_id}`}>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(l.lead_id)}
                        onCheckedChange={() => toggleSelected(l.lead_id)}
                        aria-label={`Select ${l.company_name || l.contact_email || "lead"}`}
                        data-testid={`quarantine-select-${l.lead_id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0A0A0A]">{l.company_name || "(no company)"}</div>
                      <div className="text-[11px] text-[#A3A3A3] font-mono">{l.lead_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#0A0A0A]">{l.contact_person || "—"}</div>
                      <div className="text-[11px] text-[#525252]">{l.contact_email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-[#525252] text-xs">{l.source_of_lead || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#92400E] max-w-xs">
                      <div className="italic">{l.quarantine_reason || "—"}</div>
                      {l.quarantine_rule_id && (
                        <div className="text-[10px] text-[#A3A3A3] font-mono mt-0.5">rule {l.quarantine_rule_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#A3A3A3] font-mono whitespace-nowrap">
                      {l.quarantined_at ? formatLocal(l.quarantined_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => review(l, "release")}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 mr-2"
                        data-testid={`quarantine-release-${l.lead_id}`}
                        title="Re-activate this lead and allow it into campaigns"
                      >
                        <CheckCircle size={13} weight="bold" /> Release
                      </button>
                      <button
                        onClick={() => review(l, "delete")}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                        data-testid={`quarantine-delete-${l.lead_id}`}
                        title="Permanently remove from the system"
                      >
                        <Trash size={13} weight="bold" /> Delete
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#A3A3A3]">No quarantined leads match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
